import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND, BRAND_LIGHT, getThemeColors } from '../../../lib/theme';
import {
  DEFAULT_MEDIA_DOC_META,
  defaultCategory,
  isVideoFile,
  mediaDocErrorMessage,
  mediaDocumentsApi,
  notifyMediaDocumentsChanged,
} from '../../../lib/mediaDocuments';
import { useMediaDocuments } from '../../../lib/useMediaDocuments';
import { usePermissions } from '../../../contexts/PermissionsContext';
import { PlayBadge } from '../tabs/medien/shared';
import { type UploadRow } from './types';
import { WidgetCard } from './WidgetCard';

/* ─── MedienWidget (compact) ──────────────────────────────────────────────── */

export function MedienWidget({ dark, userId, onShowAll }: {
  dark: boolean;
  userId: number;
  /** @deprecated v1-Liste — das Widget lädt selbst über /v2 (presigned URLs). */
  initialMedia?: UploadRow[];
  onShowAll: (e: React.MouseEvent) => void;
}) {
  const { t } = useTranslation();
  const tc = getThemeColors(dark);
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission('users.write');
  const { data } = useMediaDocuments(userId);
  const media = data?.media ?? [];
  const meta = data?.meta ?? DEFAULT_MEDIA_DOC_META;
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    e.stopPropagation();
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    setUploading(true); setUploadError(null);
    try {
      // Bilder → "Bilder der Person", Videos → "Videos" (je ein Request pro Kategorie).
      const videos = files.filter(isVideoFile);
      const images = files.filter(f => !isVideoFile(f));
      if (images.length) await mediaDocumentsApi.upload(userId, 'media', images, defaultCategory(images[0], 'media', meta));
      if (videos.length) await mediaDocumentsApi.upload(userId, 'media', videos, defaultCategory(videos[0], 'media', meta));
      notifyMediaDocumentsChanged(userId);
    } catch (err) {
      setUploadError(mediaDocErrorMessage(err) ?? t('pflege.detail.dashboard.upload_error'));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  }, [userId, meta, t]);

  const preview = media.slice(0, 4);
  return (
    <WidgetCard dark={dark} title={t('pflege.detail.dashboard.medien')} badge={media.length || null} onShowAll={onShowAll} showAllLabel={t('pflege.detail.dashboard.alle_anzeigen')}>
      <div style={{ padding: '10px 14px' }}>
        {preview.length === 0 ? (
          <div style={{ fontSize: 12, color: tc.muted, fontStyle: 'italic' }}>{t('pflege.media.empty_media')}</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 6 }}>
            {preview.map(item => {
              const thumb = item.thumbnail_url ?? (item.is_image ? item.url : null);
              return (
                <a
                  key={item.id}
                  href={item.url ?? undefined}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={e => e.stopPropagation()}
                  title={item.name}
                  style={{ position: 'relative', aspectRatio: '1', borderRadius: 7, border: `1px solid ${tc.border}`, background: tc.cardMuted, display: 'flex', alignItems: 'center', justifyContent: 'center', textDecoration: 'none', color: tc.muted, fontSize: 11, overflow: 'hidden' }}
                >
                  {thumb ? (
                    <img src={thumb} alt={item.name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                  ) : item.processing ? '…' : null}
                  {item.is_video && <PlayBadge size={24} />}
                </a>
              );
            })}
          </div>
        )}
      </div>
      {canWrite && (
        <>
          <input
            ref={fileInputRef}
            type="file"
            multiple
            accept={meta.accept.media.map(x => `.${x}`).join(',')}
            style={{ display: 'none' }}
            onClick={e => e.stopPropagation()}
            onChange={handleFileChange}
          />
          {uploadError && <div style={{ margin: '0 14px 4px', fontSize: 11, color: '#b42318' }}>{uploadError}</div>}
          <button type="button" onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }} disabled={uploading} style={{ margin: '4px 14px 10px', fontSize: 12, fontWeight: 600, color: BRAND, background: BRAND_LIGHT, border: `1px dashed ${BRAND}`, borderRadius: 7, padding: '5px 10px', cursor: uploading ? 'wait' : 'pointer', textAlign: 'left', opacity: uploading ? 0.7 : 1 }}>
            {uploading ? '…' : `+ ${t('pflege.detail.dashboard.medium_hinzufuegen')}`}
          </button>
        </>
      )}
    </WidgetCard>
  );
}
